import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import toast from 'react-hot-toast';
import SEO from '../components/SEO';

const Profile = () => {
  const { currentUser, userProfile, logout, fetchUserProfile } = useAuth();
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    fullName: userProfile?.fullName || '',
    phone: userProfile?.phone || '',
    address: userProfile?.address || '',
    city: userProfile?.city || '',
    state: userProfile?.state || '',
    pincode: userProfile?.pincode || ''
  });

  React.useEffect(() => {
    if (userProfile) {
      setFormData({
        fullName: userProfile.fullName || '',
        phone: userProfile.phone || '',
        address: userProfile.address || '',
        city: userProfile.city || '',
        state: userProfile.state || '',
        pincode: userProfile.pincode || ''
      });
    }
  }, [userProfile]);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSaveProfile = async () => {
    setIsSaving(true);
    try {
      await updateDoc(doc(db, 'users', currentUser.uid), {
        fullName: formData.fullName,
        phone: formData.phone,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        pincode: formData.pincode,
        updatedAt: new Date()
      });
      await fetchUserProfile(currentUser.uid);
      setIsEditing(false);
      toast.success('Profile updated successfully!');
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const profileOptions = [
    {
      title: 'My Orders',
      description: 'Track and manage your orders',
      icon: '📦',
      link: '/orders',
      color: 'sage'
    },
    {
      title: 'Request Data Deletion',
      description: 'Request deletion of your personal data',
      icon: '🗑️',
      link: '/delete-user-data',
      color: 'red-500'
    },
    {
      title: 'Size Guide',
      description: 'Find your perfect fit',
      icon: '📏',
      link: '/size-guide',
      color: 'gold'
    },
    {
      title: 'Privacy Policy',
      description: 'View our privacy policy',
      icon: '🔒',
      link: '/privacy-policy',
      color: 'warm-gray'
    }
  ];

  return (
    <>
      <SEO
        title="My Profile | House of Deepak"
        description="View and manage your House of Deepak account details, addresses, and orders."
        keywords="profile, account, user settings, house of deepak"
        canonical="/profile"
      />
      <div className="min-h-screen bg-cream pt-20 pb-16">
        <div className="section-padding">
          <div className="container-max max-w-6xl">
            {/* Header */}
            <motion.div
              className="mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <h1 className="text-3xl md:text-4xl font-serif text-deep-brown mb-2">
                My Profile
              </h1>
              <p className="text-warm-gray">
                Manage your account information and settings
              </p>
            </motion.div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Account Information */}
              <div className="lg:col-span-2">
                <div className="bg-white rounded-lg shadow-sm border border-warm-gray/20 p-6">
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-xl font-serif text-deep-brown">
                      Account Information
                    </h2>
                    {!isEditing ? (
                      <button
                        onClick={() => setIsEditing(true)}
                        className="text-gold hover:text-deep-brown transition-colors duration-200 font-medium"
                      >
                        Edit Profile
                      </button>
                    ) : (
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            setIsEditing(false);
                            setFormData({
                              fullName: userProfile?.fullName || '',
                              phone: userProfile?.phone || '',
                              address: userProfile?.address || '',
                              city: userProfile?.city || '',
                              state: userProfile?.state || '',
                              pincode: userProfile?.pincode || ''
                            });
                          }}
                          className="text-warm-gray hover:text-deep-brown transition-colors duration-200"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleSaveProfile}
                          disabled={isSaving}
                          className="text-sage hover:text-deep-brown transition-colors duration-200 font-medium disabled:opacity-50"
                        >
                          {isSaving ? 'Saving...' : 'Save'}
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="space-y-4">
                    {/* Email (read-only) */}
                    <div>
                      <label className="block text-sm font-medium text-deep-brown mb-2">
                        Email Address
                      </label>
                      <input
                        type="email"
                        value={currentUser?.email || ''}
                        disabled
                        className="w-full px-3 py-2 border border-warm-gray/30 rounded-md bg-gray-50 text-warm-gray cursor-not-allowed"
                      />
                      <p className="mt-1 text-xs text-warm-gray">
                        Email cannot be changed
                      </p>
                    </div>

                    {/* Full Name */}
                    <div>
                      <label className="block text-sm font-medium text-deep-brown mb-2">
                        Full Name
                      </label>
                      <input
                        type="text"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        className="w-full px-3 py-2 border border-warm-gray/30 rounded-md focus:outline-none focus:ring-gold focus:border-gold disabled:bg-gray-50 disabled:text-warm-gray"
                      />
                    </div>

                    {/* Phone */}
                    <div>
                      <label className="block text-sm font-medium text-deep-brown mb-2">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        className="w-full px-3 py-2 border border-warm-gray/30 rounded-md focus:outline-none focus:ring-gold focus:border-gold disabled:bg-gray-50 disabled:text-warm-gray"
                      />
                    </div>

                    {/* Address */}
                    <div>
                      <label className="block text-sm font-medium text-deep-brown mb-2">
                        Address
                      </label>
                      <textarea
                        name="address"
                        value={formData.address}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        rows={3}
                        className="w-full px-3 py-2 border border-warm-gray/30 rounded-md focus:outline-none focus:ring-gold focus:border-gold disabled:bg-gray-50 disabled:text-warm-gray resize-none"
                      />
                    </div>

                    {/* City, State, Pincode */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-deep-brown mb-2">
                          City
                        </label>
                        <input
                          type="text"
                          name="city"
                          value={formData.city}
                          onChange={handleInputChange}
                          disabled={!isEditing}
                          className="w-full px-3 py-2 border border-warm-gray/30 rounded-md focus:outline-none focus:ring-gold focus:border-gold disabled:bg-gray-50 disabled:text-warm-gray"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-deep-brown mb-2">
                          State
                        </label>
                        <input
                          type="text"
                          name="state"
                          value={formData.state}
                          onChange={handleInputChange}
                          disabled={!isEditing}
                          className="w-full px-3 py-2 border border-warm-gray/30 rounded-md focus:outline-none focus:ring-gold focus:border-gold disabled:bg-gray-50 disabled:text-warm-gray"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-deep-brown mb-2">
                          Pincode
                        </label>
                        <input
                          type="text"
                          name="pincode"
                          value={formData.pincode}
                          onChange={handleInputChange}
                          disabled={!isEditing}
                          maxLength={6}
                          className="w-full px-3 py-2 border border-warm-gray/30 rounded-md focus:outline-none focus:ring-gold focus:border-gold disabled:bg-gray-50 disabled:text-warm-gray"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick Actions Sidebar */}
              <div className="space-y-6">
                {/* Account Actions */}
                <div className="bg-white rounded-lg shadow-sm border border-warm-gray/20 p-6">
                  <h3 className="text-lg font-serif text-deep-brown mb-4">
                    Quick Actions
                  </h3>
                  <div className="space-y-3">
                    {profileOptions.map((option, idx) => (
                      <Link
                        key={idx}
                        to={option.link}
                        className="flex items-start p-3 rounded-md hover:bg-cream transition-colors duration-200 group"
                      >
                        <span className="text-2xl mr-3">{option.icon}</span>
                        <div>
                          <p className="text-sm font-medium text-deep-brown group-hover:text-gold">
                            {option.title}
                          </p>
                          <p className="text-xs text-warm-gray">
                            {option.description}
                          </p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>

                {/* Account Stats */}
                <div className="bg-white rounded-lg shadow-sm border border-warm-gray/20 p-6">
                  <h3 className="text-lg font-serif text-deep-brown mb-4">
                    Account Details
                  </h3>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-warm-gray">Member Since</span>
                      <span className="text-deep-brown font-medium">
                        {userProfile?.createdAt
                          ? new Date(userProfile.createdAt).toLocaleDateString('en-IN', {
                              month: 'short',
                              year: 'numeric'
                            })
                          : 'N/A'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-warm-gray">Account Type</span>
                      <span className="text-deep-brown font-medium capitalize">
                        {userProfile?.role || 'Customer'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Logout Button */}
                <button
                  onClick={handleLogout}
                  className="w-full bg-red-500 text-white py-3 rounded-md font-medium hover:bg-red-600 transition-colors duration-300"
                >
                  Logout
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Profile;
